import { Review } from "../modules/shared/model";

/**
 * Calculate average rating from an array of reviews
 * @param reviews Array of reviews
 * @returns Object containing average rating and total count
 */
export const calculateAverageRating = (reviews: Review[]): { average: number; count: number } => {
  if (!reviews || reviews.length === 0) {
    return { average: 0, count: 0 };
  }

  // Filter out reviews with null/undefined ratings
  const validReviews = reviews.filter(review => review.rating && review.rating > 0);
  
  if (validReviews.length === 0) {
    return { average: 0, count: 0 };
  }

  const totalRating = validReviews.reduce((sum, review) => sum + (review.rating || 0), 0);
  const average = parseFloat((totalRating / validReviews.length).toFixed(1));
  
  return { average, count: validReviews.length };
};

/**
 * Calculate average rating for a specific user from reviews where they are the user being reviewed
 * @param reviews Array of all reviews
 * @param userId ID of the user to calculate rating for
 * @returns Object containing average rating and total count
 */
export const calculateUserAverageRating = (reviews: Review[], userId: string): { average: number; count: number } => {
  if (!reviews || reviews.length === 0 || !userId) {
    return { average: 0, count: 0 };
  }
console.log("calculateUserAverageRating - reviews:", reviews, "userId:", userId);
  // Filter reviews where the user is being reviewed (review.user.id === userId)
  const userReviews = reviews.filter(review => 
    review.user?.id === userId && review.rating && review.rating > 0
  );
  
  return calculateAverageRating(userReviews);
};

/**
 * Calculate average rating for a specific author from reviews they have written
 * @param reviews Array of all reviews
 * @param authorId ID of the author to calculate rating for
 * @returns Object containing average rating and total count
 */
export const calculateAuthorAverageRating = (reviews: Review[], authorId: string): { average: number; count: number } => {
  if (!reviews || reviews.length === 0 || !authorId) {
    return { average: 0, count: 0 };
  }

  // Filter reviews where the author is the one who wrote the review (review.author.id === authorId)
  const authorReviews = reviews.filter(review => 
    review.author?.id === authorId && review.rating && review.rating > 0
  );
  
  return calculateAverageRating(authorReviews);
};

/**
 * Get reviews for a specific milestone
 * @param reviews Array of all reviews
 * @param milestoneId ID of the milestone
 * @returns Array of reviews for the milestone
 */
export const getMilestoneReviews = (reviews: Review[], milestoneId: string): Review[] => {
  if (!reviews || reviews.length === 0 || !milestoneId) {
    return [];
  }

  return reviews.filter(review => review.milestone?.id === milestoneId);
};

/**
 * Calculate average rating for a specific milestone
 * @param reviews Array of all reviews
 * @param milestoneId ID of the milestone
 * @returns Object containing average rating and total count
 */
export const calculateMilestoneAverageRating = (reviews: Review[], milestoneId: string): { average: number; count: number } => {
  const milestoneReviews = getMilestoneReviews(reviews, milestoneId);
  return calculateAverageRating(milestoneReviews);
};
